import cx from 'classnames'
import { useState } from 'react'

import { Logo } from './logo.component'
import styles from './logo.gallery.module.css'
import type { LogoBrand } from './logo.types'

const BRANDS: LogoBrand[] = [
  'cinch',
  'Marshall',
  'cinchLeasing',
  'cinchServicing',
]

const HEIGHTS = [
  { label: '20px', className: styles.height20 },
  { label: 'Figma (33px)', className: undefined },
  { label: '48px', className: styles.height48 },
]

/**
 * Development-only gallery rendering every Logo variant.
 *
 * Mirrors the Figma component set so the implementation can be compared
 * against the design side by side.
 *
 * @see https://www.figma.com/design/uoYtLe77tOoT3iN5hpDfv5?node-id=10189-1773
 */
export const LogoGallery = () => {
  const [showOutlines, setShowOutlines] = useState(false)

  return (
    <main className={cx(styles.page, showOutlines && styles.outlined)}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Design System V3</p>
        <h1 className={styles.title}>Logo</h1>
        <p className={styles.lede}>
          Every variant the component supports: {BRANDS.length} brands &times; 2
          backgrounds = {BRANDS.length * 2} logos, each at its Figma size. Hover
          any logo to see its exact variant.
        </p>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showOutlines}
            onChange={(event) => setShowOutlines(event.target.checked)}
          />
          Outline each logo box
        </label>
      </header>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Next to a hidden logo</h2>
        <div className={cx(styles.example, styles.light)}>
          {/* The first logo on the page, hidden like a header logo at this
              breakpoint. If logos shared a gradient id, every visible cinch
              logo below would lose its wordmark in Chrome and Firefox. */}
          <div className={styles.hidden}>
            <Logo brand="cinch" />
          </div>
          <Logo brand="cinch" className={styles.height48} />
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Variants</h2>
        <div className={styles.variants}>
          <span className={styles.columnLabel}>On = Light BG</span>
          <span className={styles.columnLabel}>
            On = Dark BG (<code className={styles.code}>inverted</code>)
          </span>
          {BRANDS.map((brand) => (
            <div key={brand} className={styles.row}>
              {/* An svg title attribute shows no tooltip, so the cell has it. */}
              <div className={styles.light} title={`${brand} / Light BG`}>
                <Logo brand={brand} />
              </div>
              <div className={styles.dark} title={`${brand} / Dark BG`}>
                <Logo brand={brand} inverted />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Resized with <code className={styles.code}>className</code>
        </h2>
        <div className={cx(styles.example, styles.dark)}>
          {HEIGHTS.map(({ label, className }) => (
            <figure key={label} className={styles.sample}>
              <Logo brand="cinchServicing" inverted className={className} />
              <figcaption className={styles.caption}>{label}</figcaption>
            </figure>
          ))}
        </div>
      </section>
    </main>
  )
}
